/** Copy text with the async Clipboard API and a legacy browser fallback. */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.readOnly = true;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand("copy");
    textarea.remove();
    return copied;
  } catch {
    return false;
  }
}

/**
 * Save `text` as a file the visitor can keep. Creating a temporary anchor is
 * the only portable way to name a download; the object URL is revoked straight
 * after so the blob isn't retained for the life of the document.
 *
 * Lives here beside `copyTextToClipboard` because they're the same kind of
 * thing — a one-shot browser side effect on a string.
 */
export function downloadTextFile(
  text: string,
  filename: string,
  mimeType = "text/plain;charset=utf-8",
): void {
  const url = URL.createObjectURL(new Blob([text], { type: mimeType }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

/**
 * Download `csv` as a UTF-8 CSV file. The leading BOM makes spreadsheet apps
 * read non-Latin text as UTF-8 instead of the system code page.
 */
export function downloadCsvFile(csv: string, filename: string): void {
  downloadTextFile(`\uFEFF${csv}`, filename, "text/csv;charset=utf-8");
}
